import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Radio,
  ClipboardCheck,
  Send,
  CheckSquare,
  Archive,
  Settings,
  LogOut,
  FolderKanban,
  ScrollText,
  Flame,
  Bell,
  FileEdit,
  FileText,
  GitBranch,
  Building2,
  Activity,
  GraduationCap,
  FileCheck,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import { useFeedStore } from '@/stores/feedStore';
import { UserRole, type Permission } from '@/types/auth';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { api } from '@/lib/api';
import { NotificationBell } from './NotificationBell';

interface NavItem {
  readonly label: string;
  readonly path: string;
  readonly icon: typeof LayoutDashboard;
  readonly requiredPermission?: Permission;
  readonly requiredRoles?: readonly UserRole[];
  readonly badgeKey?: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  {
    label: 'Dashboard',
    path: '/',
    icon: LayoutDashboard,
  },
  {
    label: 'Live Feed',
    path: '/feed',
    icon: Radio,
    badgeKey: 'newEvents',
  },
  {
    label: 'Review Queue',
    path: '/review',
    icon: ClipboardCheck,
    badgeKey: 'pendingReviews',
  },
  {
    label: 'Publish Queue',
    path: '/publish',
    icon: Send,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
    badgeKey: 'queuedPublish',
  },
  {
    label: 'Compliance',
    path: '/projects',
    icon: FolderKanban,
    badgeKey: 'activeProjects',
  },
  {
    label: 'Policy Review',
    path: '/policy-review',
    icon: FileEdit,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
    badgeKey: 'policyReviewSessions',
  },
  {
    label: 'Document Library',
    path: '/document-library',
    icon: FileText,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
  },
  {
    label: 'SOP Cascades',
    path: '/cascades',
    icon: GitBranch,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
  },
  {
    label: 'My Training',
    path: '/training',
    icon: GraduationCap,
    badgeKey: 'pendingTraining',
  },
  {
    label: 'Training Dashboard',
    path: '/training/dashboard',
    icon: Activity,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
  },
  {
    label: 'Training Evidence',
    path: '/training/evidence',
    icon: FileCheck,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
  },
  {
    label: 'Attestations',
    path: '/attestations',
    icon: CheckSquare,
    badgeKey: 'overdueAttestations',
  },
  {
    label: 'Escalations',
    path: '/escalations',
    icon: Flame,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
    badgeKey: 'openEscalations',
  },
  {
    label: 'Notifications',
    path: '/notifications',
    icon: Bell,
    badgeKey: 'unreadNotifications',
  },
  {
    label: 'Audit Trail',
    path: '/audit-trail',
    icon: ScrollText,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
  },
  {
    label: 'Archive',
    path: '/archive',
    icon: Archive,
    requiredRoles: [UserRole.CCO, UserRole.COMPLIANCE_REP],
  },
  {
    label: 'Compliance Score',
    path: '/dashboard/score',
    icon: Activity,
  },
  {
    label: 'Organization',
    path: '/admin/organization',
    icon: Building2,
    requiredRoles: [UserRole.CCO, UserRole.ADMIN],
  },
  {
    label: 'Admin',
    path: '/admin',
    icon: Settings,
    requiredRoles: [UserRole.ADMIN],
  },
];

function getRoleBadge(role: UserRole): string {
  switch (role) {
    case UserRole.CCO:
      return 'CCO';
    case UserRole.COMPLIANCE_REP:
      return 'Compliance';
    case UserRole.DEPARTMENT_REP:
      return 'Department';
    case UserRole.ADMIN:
      return 'Admin';
  }
}

export function Sidebar({ onNavClick }: { readonly onNavClick?: () => void } = {}) {
  const { user, clearUser } = useAuthStore();
  const { newEventCount, connected } = useFeedStore();
  const navigate = useNavigate();

  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data: pendingReviews } = useQuery({
    queryKey: ['nav-review-count'],
    queryFn: () => api.reviewSessions.list({ status: 'pending', limit: 1 }),
    staleTime: 60_000,
    refetchInterval: 120_000,
    enabled: !!user,
  });
  const pendingReviewCount = pendingReviews?.total ?? 0;

  const { data: publishQueue } = useQuery({
    queryKey: ['nav-publish-count'],
    queryFn: () => api.publishQueue.list({ status: 'queued', limit: 1 }),
    staleTime: 60_000,
    refetchInterval: 120_000,
    enabled: !!user && hasPermission('bulk_publish'),
  });
  const publishCount = publishQueue?.total ?? 0;

  const { data: activeProjects } = useQuery({
    queryKey: ['nav-active-projects'],
    queryFn: () => api.projects.summary(),
    staleTime: 60_000,
    refetchInterval: 120_000,
    enabled: !!user,
  });
  const activeProjectCount = (activeProjects?.open ?? 0) + (activeProjects?.inProgress ?? 0);

  const { data: overdueAttestations } = useQuery({
    queryKey: ['nav-attestation-count'],
    queryFn: () => api.attestations.list({ status: 'overdue', limit: 1 }),
    staleTime: 60_000,
    refetchInterval: 120_000,
    enabled: !!user,
  });
  const overdueCount = overdueAttestations?.total ?? 0;

  const { data: openEscalations } = useQuery({
    queryKey: ['nav-escalation-count'],
    queryFn: () => api.escalations.list(),
    staleTime: 60_000,
    refetchInterval: 120_000,
    enabled: !!user && hasPermission('view_all_departments'),
  });
  const openEscalationCount = openEscalations?.escalations?.filter((e) => e.status === 'open').length ?? 0;

  const { data: policyReviewData } = useQuery({
    queryKey: ['nav-policy-review-count'],
    queryFn: () => api.policyReview.sessions({ status: 'in_review' }),
    staleTime: 60_000,
    refetchInterval: 120_000,
    enabled: !!user && hasPermission('view_all_departments'),
  });
  const policyReviewCount = policyReviewData?.sessions?.length ?? 0;

  const { data: myTraining } = useQuery({
    queryKey: ['nav-my-training'],
    queryFn: () => api.training.myTraining(),
    staleTime: 60_000,
    refetchInterval: 120_000,
    enabled: !!user,
  });
  const pendingTrainingCount = (myTraining?.data?.pending?.length ?? 0) + (myTraining?.data?.overdue?.length ?? 0);

  const { data: unreadNotifications } = useQuery({
    queryKey: ['notification-unread-count'],
    queryFn: () => api.notifications.unreadCount(),
    staleTime: 30_000,
    refetchInterval: 60_000,
    enabled: !!user,
  });
  const unreadNotificationCount = unreadNotifications?.count ?? 0;

  const handleLogout = async () => {
    try {
      await api.auth.logout();
    } finally {
      clearUser();
      navigate('/login');
    }
  };

  const isItemVisible = (item: NavItem): boolean => {
    if (!user) return false;
    if (!item.requiredRoles) return true;
    return item.requiredRoles.includes(user.role);
  };

  return (
    <aside className="flex h-full w-60 flex-col border-r border-border bg-sidebar">
      {/* Logo */}
      <div className="p-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold tracking-tight text-teal">
            NOETED
          </h1>
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              connected ? 'bg-compliance-low' : 'bg-compliance-critical',
            )}
          />
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          Demo Health System
        </p>
      </div>

      <Separator />

      {/* Navigation */}
      <nav className="flex-1 space-y-1 p-3">
        {NAV_ITEMS.filter(isItemVisible).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            onClick={onNavClick}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-l-2 border-teal bg-teal/10 text-teal-light'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{item.label}</span>
            {item.badgeKey === 'newEvents' && newEventCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-teal px-1.5 text-[10px] font-bold text-white">
                {newEventCount > 99 ? '99+' : newEventCount}
              </span>
            )}
            {item.badgeKey === 'pendingReviews' && pendingReviewCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-compliance-medium px-1.5 text-[10px] font-bold text-white">
                {pendingReviewCount > 99 ? '99+' : pendingReviewCount}
              </span>
            )}
            {item.badgeKey === 'queuedPublish' && publishCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-teal px-1.5 text-[10px] font-bold text-white">
                {publishCount > 99 ? '99+' : publishCount}
              </span>
            )}
            {item.badgeKey === 'activeProjects' && activeProjectCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-teal px-1.5 text-[10px] font-bold text-white">
                {activeProjectCount > 99 ? '99+' : activeProjectCount}
              </span>
            )}
            {item.badgeKey === 'policyReviewSessions' && policyReviewCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-teal px-1.5 text-[10px] font-bold text-white">
                {policyReviewCount > 99 ? '99+' : policyReviewCount}
              </span>
            )}
            {item.badgeKey === 'overdueAttestations' && overdueCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-compliance-critical px-1.5 text-[10px] font-bold text-white">
                {overdueCount > 99 ? '99+' : overdueCount}
              </span>
            )}
            {item.badgeKey === 'openEscalations' && openEscalationCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-compliance-high px-1.5 text-[10px] font-bold text-white">
                {openEscalationCount > 99 ? '99+' : openEscalationCount}
              </span>
            )}
            {item.badgeKey === 'pendingTraining' && pendingTrainingCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-teal-600 px-1.5 text-[10px] font-bold text-white">
                {pendingTrainingCount > 99 ? '99+' : pendingTrainingCount}
              </span>
            )}
            {item.badgeKey === 'unreadNotifications' && unreadNotificationCount > 0 && (
              <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-teal px-1.5 text-[10px] font-bold text-white">
                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      {user && (
        <>
          <Separator />
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal/20 text-sm font-semibold text-teal-light">
                {user.name?.charAt(0)?.toUpperCase() ?? '?'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {user.name}
                </p>
                <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-light bg-teal/10">
                  {getRoleBadge(user.role)}
                </span>
              </div>
              <NotificationBell />
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={handleLogout}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </Button>
          </div>
        </>
      )}
    </aside>
  );
}
